import { gerarPlanoSessao, transicionarPlano, aplicarResultadoPlano, normalizarMetasPEI, SESSION_PLAN_STATUS } from '../../lib/planner.js';
import { LocalStorageRepository, normalizeSnapshot } from '../../repositories/LocalStorageRepository.js';
import { scopedStorageKey } from '../../lib/deployment.js';
import { caseStudyForStudent } from '../case-study/case-study.js';

const STORAGE_KEY = scopedStorageKey('sgaee.sessionPlans.v18');
const LEGACY_STORAGE_KEY = scopedStorageKey('sgaee.sessionPlans.v17');

export function loadSessionPlans(storage = globalThis.localStorage) {
  if (!storage) return [];
  try {
    const raw = storage.getItem(STORAGE_KEY) || storage.getItem(LEGACY_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.warn('Não foi possível carregar planos de atendimento.', error);
    return [];
  }
}

export function saveSessionPlans(planos, storage = globalThis.localStorage) {
  if (!storage) return planos;
  storage.setItem(STORAGE_KEY, JSON.stringify(planos));
  globalThis.dispatchEvent?.(new CustomEvent('sgaee:session-plans-updated', { detail: planos }));
  return planos;
}

export function upsertSessionPlan(plano, storage = globalThis.localStorage) {
  const atuais = loadSessionPlans(storage).filter((item) => item.id !== plano.id);
  const atualizados = saveSessionPlans([...atuais, plano], storage);
  if (plano.status === SESSION_PLAN_STATUS.REGISTRADO) syncRegisteredPlanToStudentHistory(plano, storage);
  return atualizados;
}

export function getPlansByDate(data, storage = globalThis.localStorage) {
  return loadSessionPlans(storage).filter((plano) => plano.data === data);
}

function metasDoAluno(peis = [], alunoId) {
  return normalizarMetasPEI(peis.filter((pei) => pei.alunoId === alunoId || pei.studentId === alunoId));
}

export function gerarPlanosDoDia({ alunos = [], bancoAtividades = [], avaliacoes = [], logs = [], peis = [], caseStudies = [], profissionalId, data = new Date().toISOString().slice(0, 10) }) {
  return alunos.map((aluno) => {
    const plano = gerarPlanoSessao({
      aluno,
      profissionalId,
      bancoAtividades,
      avaliacaoMaisRecente: avaliacoes.find((avaliacao) => avaliacao.alunoId === aluno.id || avaliacao.studentId === aluno.id),
      historicoLogs: logs.filter((log) => log.alunoId === aluno.id || log.studentId === aluno.id),
      metasPEI: metasDoAluno(peis, aluno.id),
      caseStudy: caseStudyForStudent(caseStudies, aluno.id),
      data,
    });
    return { ...plano, data, alunoNome: aluno.nome || aluno.name || aluno.id };
  });
}

export function syncRegisteredPlanToStudentHistory(plano, storage = globalThis.localStorage) {
  if (!storage || plano?.status !== SESSION_PLAN_STATUS.REGISTRADO || !plano.alunoId) return null;
  const repository = new LocalStorageRepository(storage);
  const snapshot = normalizeSnapshot(repository.load());
  const logs = Array.isArray(snapshot.logs) ? snapshot.logs : [];
  const itens = plano.itens || [];
  const resultados = plano.resultado?.itens || [];
  const resultadoPorAtividade = new Map(resultados.map((item) => [item.atividadeId, item]));
  const log = {
    id: `log_${String(plano.id).replace(/[^\w.-]/g, '_')}`,
    studentId: plano.alunoId,
    data: plano.data || new Date().toISOString().slice(0, 10),
    autorUid: plano.profissionalId || '',
    objetivo: plano.objetivoSessao || 'Plano aplicado pelo assistente pessoal do professor.',
    estrategia: itens.map((item) => item.instancia?.roteiro?.map((step) => step.acao).join(' | ') || item.tituloSnapshot).filter(Boolean).join('\n'),
    observacao: plano.resultado?.observacaoGeral || '',
    planId: plano.id,
    atividadeIds: itens.map((item) => item.atividadeId).filter(Boolean),
    habilidades: [...new Set(itens.flatMap((item) => item.habilidadesAlvo || []))],
    comboKeys: itens.map((item) => item.comboKey).filter(Boolean),
    itens: itens.map((item) => ({
      atividadeId: item.atividadeId,
      titulo: item.tituloSnapshot,
      habilidades: item.habilidadesAlvo || [],
      resultado: resultadoPorAtividade.get(item.atividadeId)?.desempenho || '',
      score: resultadoPorAtividade.get(item.atividadeId)?.score ?? null,
      apoio: resultadoPorAtividade.get(item.atividadeId)?.apoio ?? null,
      tentativas: resultadoPorAtividade.get(item.atividadeId)?.tentativas ?? null,
      adaptacao: resultadoPorAtividade.get(item.atividadeId)?.adaptacao || '',
      contexto: resultadoPorAtividade.get(item.atividadeId)?.contexto || '',
      motivoInterrupcao: resultadoPorAtividade.get(item.atividadeId)?.motivoInterrupcao || '',
      obs: resultadoPorAtividade.get(item.atividadeId)?.obs || '',
      proximoPasso: resultadoPorAtividade.get(item.atividadeId)?.proximoPasso || '',
      generalizacao: resultadoPorAtividade.get(item.atividadeId)?.generalizacao || [],
      participacao: resultadoPorAtividade.get(item.atividadeId)?.participacao || null,
      comboKey: item.comboKey || item.instancia?.comboKey || null,
      instancia: item.instancia || null,
    })),
    origem: 'copiloto_professor_v18',
  };
  const next = repository.save({ ...snapshot, logs: [log, ...logs.filter((item) => item.planId !== plano.id)] });
  globalThis.dispatchEvent?.(new CustomEvent('sgaee:data-updated', { detail: next }));
  return next;
}

export function aprovarPlano(plano) { return upsertSessionPlan(transicionarPlano(plano, SESSION_PLAN_STATUS.APROVADO)); }
export function marcarPlanoAplicado(plano) { return upsertSessionPlan(transicionarPlano(plano, SESSION_PLAN_STATUS.APLICADO)); }
export function registrarResultadoPlano(plano, resultado) { return upsertSessionPlan(aplicarResultadoPlano(plano, resultado)); }
export { STORAGE_KEY, SESSION_PLAN_STATUS };
